import { useEffect, useRef, useState } from 'react';
import { Loader2, Pause, Play, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { audioService } from '@/services/audio.service';
import { cn } from '@/lib/cn';

type AudioStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

/**
 * "Read aloud" control for a book's description. On first press it asks the
 * API for the ElevenLabs-synthesized MP3 (generated + cached server-side),
 * then plays it via a native `HTMLAudioElement`. Subsequent presses toggle
 * play/pause.
 *
 * Read-aloud is free for everyone — it's a small accessibility win for early
 * and reluctant readers, so we don't gate it behind a subscription.
 *
 * The control resets when `bookId` changes so reusing the modal for a
 * different book (e.g. a "similar books" click) never leaves stale audio
 * playing.
 */
export function DescriptionAudioButton({ bookId }: { bookId: string }) {
  const [status, setStatus] = useState<AudioStatus>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Tear the audio element down on unmount or whenever the book changes.
  // Detach the handlers *before* clearing the source: emptying an
  // <audio> element's src fires an `error` event, and we don't want that
  // teardown signal to reach the error handler (which would pop a toast as
  // the modal closes).
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.onended = null;
        audio.onerror = null;
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        audioRef.current = null;
      }
    };
  }, [bookId]);

  // Reset visible state when switching books (the cleanup above runs first).
  useEffect(() => {
    setStatus('idle');
  }, [bookId]);

  const handleClick = async () => {
    // Toggle an already-loaded clip without re-fetching.
    const existing = audioRef.current;
    if (existing) {
      if (status === 'playing') {
        existing.pause();
        setStatus('paused');
      } else {
        void existing.play().catch(() => setStatus('error'));
        setStatus('playing');
      }
      return;
    }

    // First press: fetch (and lazily synthesize) the audio, then play.
    setStatus('loading');
    try {
      const { url } = await audioService.getDescriptionAudio(bookId);
      const audio = new Audio(url);
      // Assignable handler properties (not addEventListener) so teardown
      // can null them out before clearing the source — see the cleanup
      // effect above.
      audio.onended = () => setStatus('idle');
      audio.onerror = () => {
        setStatus('error');
        toast.error('Could not play the audio. Please try again.');
      };
      audioRef.current = audio;
      await audio.play();
      setStatus('playing');
    } catch {
      audioRef.current = null;
      setStatus('error');
      toast.error('Could not load the read-aloud audio. Please try again.');
    }
  };

  const { icon, label } = describe(status);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={status === 'loading'}
      aria-label={status === 'playing' ? 'Pause read aloud' : 'Listen to the book summary'}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-or-orange-200 bg-white px-3 py-1 text-[12px] font-semibold text-or-orange-700 shadow-or-card transition',
        'hover:bg-or-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40',
        'disabled:cursor-default disabled:opacity-70',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function describe(status: AudioStatus): { icon: React.ReactNode; label: string } {
  switch (status) {
    case 'loading':
      return { icon: <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />, label: 'Loading…' };
    case 'playing':
      return { icon: <Pause className="h-3.5 w-3.5" aria-hidden />, label: 'Pause' };
    case 'paused':
      return { icon: <Play className="h-3.5 w-3.5" aria-hidden />, label: 'Resume' };
    case 'error':
      return { icon: <Volume2 className="h-3.5 w-3.5" aria-hidden />, label: 'Try again' };
    case 'idle':
    default:
      return { icon: <Volume2 className="h-3.5 w-3.5" aria-hidden />, label: 'Listen' };
  }
}
